import Heading from '@/components/Heading';
import Code from '@/components/Code';
import Link from '@/components/Link';
import DatePicker from '@/components/DatePicker';

<Heading level={2}>Standalone DatePicker Example</Heading>

In any other case unrelated to using a form to send data to the server (e.g. as part of in-browser interactions), you can use the standalone version of the DatePicker component.


<div className="card bg-base-200 shadow-sm max-w-md">
  <div className="card-body">
    <DatePicker
      label={props.label}
      value={props.value}
      onChange={props.onChange}
      isDisabled={props.isDisabled}
      isLoading={props.isLoading}
      description={props.description}
      granularity={props.granularity}
    />
  </div>
</div>

<details open>
<summary>Source Code</summary>

<Code
  readOnly={true}
  language="tsx"
  value={`import { useState } from 'react';
  import dayjs from 'dayjs';
  import DatePicker from '@/components/DatePicker';

  /* ... */

    const [value, setValue] = useState(null);

    const handleChange = (newValue) => {
      setValue(newValue);
    };

  /* ... */

      <DatePicker
        label="${props.label}"
        value={value}
        isLoading={${props.isLoading ? 'true' : 'false'}}
        isDisabled={${props.isDisabled ? 'true' : 'false'}}
        description="${props.description || ''}"
        granularity="${props.granularity}"
        onChange={handleChange}
      />
  `}
/>
</details> 

<Heading level={2}>Standalone DatePicker Props</Heading>

<div className="table-container">
  <table className="table table-zebra">
    <thead>
      <tr>
        <th>Prop</th>
        <th>Type</th>
        <th>Required</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><code>value</code></td>
        <td><code>DateValue</code> | <code>Date</code> | <code>null</code></td>
        <td>❌</td>
        <td>The current value of the date picker</td>
      </tr>
      <tr>
        <td><code>onChange</code></td>
        <td><code>(value: Date | null) =&gt; void</code></td>
        <td>❌</td>
        <td>Handler called when the date value changes</td>
      </tr>
      <tr>
        <td><code>label</code></td>
        <td><code>string</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>The label text displayed above the date picker</td>
      </tr>
      <tr>
        <td><code>description</code></td>
        <td><code>string</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Optional description text displayed below the date picker</td>
      </tr>
      <tr>
        <td><code>errorMessage</code></td>
        <td><code>string</code> | <code>function</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Error message to display. Can be a string or function that receives validation result</td>
      </tr>
      <tr>
        <td><code>isLoading</code></td>
        <td><code>boolean</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Shows skeleton loading state when true</td>
      </tr>
      <tr>
        <td><code>isDisabled</code></td>
        <td><code>boolean</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Disables the date picker when true</td>
      </tr>
      <tr>
        <td><code>granularity</code></td>
        <td><code>'day'</code> | <code>'hour'</code> | <code>'minute'</code> | <code>'second'</code></td>
        <td>❌</td>
        <td>Controls the precision of the date picker</td>
      </tr>
      <tr>
        <td><code>minValue</code></td>
        <td><code>DateValue</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>The minimum allowed date</td>
      </tr>
      <tr>
        <td><code>maxValue</code></td>
        <td><code>DateValue</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>The maximum allowed date</td>
      </tr>
      <tr>
        <td><code>isDateUnavailable</code></td>
        <td><code>(date: DateValue) =&gt; boolean</code></td>
        <td>❌</td>
        <td>Function to determine if a date should be unavailable for selection</td>
      </tr>
      <tr>
        <td><code>placeholderValue</code></td>
        <td><code>DateValue</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>A placeholder date that controls the format when no value is selected</td>
      </tr>
    </tbody>
  </table>
</div>

<div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 dark:border-blue-600 mt-8 p-4 text-blue-700 dark:text-blue-300">
  <p className="font-medium">Developer Note:</p>
  <p className="text-sm">This component extends React Aria's DatePickerProps and provides additional features like loading states and error handling. For form usage, see the Form DatePicker example above.</p>
  <p className="text-sm">You can read more about the underlying props in the <Link to="https://react-spectrum.adobe.com/react-aria/DatePicker.html#props" external={true}>React Aria DatePicker component</Link> documentation.</p>
</div>

<Heading level={2}>Date Value Types</Heading>

The DatePicker works with standard JavaScript `Date` objects:

- `Date` - A standard JavaScript Date object with time and timezone support

<Code
  readOnly={true}
  language="tsx"
  value={`import dayjs from '@/util/dayjs';

// Just a date
const birthDate = new Date(1990, 5, 15);
const parsedDate = dayjs('2023-12-25').toDate();

// Date with time
const eventDateTime = dayjs('2023-12-25T14:30:00').toDate();

// Date with timezone
const eventWithTimezone = dayjs.tz('2023-12-25T14:30:00', 'America/New_York').toDate();`}
/>

<Heading level={2}>International Support</Heading>

The DatePicker automatically formats dates according to the user's locale and supports multiple calendar systems:

<Code
  readOnly={true}
  language="tsx"
  value={`import { I18nProvider } from 'react-aria';

<I18nProvider locale="es-ES">
  <DatePicker label="Event Date" />
</I18nProvider>`}
/>

<div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 dark:border-blue-600 mt-8 p-4 text-blue-700 dark:text-blue-300">
  <p className="font-medium">International Calendars:</p>
  <p className="text-sm">Supports Gregorian, Hebrew, Indian, Islamic, Buddhist, and other calendar systems used worldwide.</p>
</div>